// src/pages/SurveyDetail.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, Card, CardContent, Chip, Divider,
  IconButton, Link as MLink, MenuItem, Select, Snackbar, Stack,
  TextField, Tooltip, Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import EditIcon from "@mui/icons-material/Edit";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PublishIcon from "@mui/icons-material/Publish";
import UnpublishedIcon from "@mui/icons-material/Unpublished";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { DataGrid, GridColDef, GridToolbar } from "@mui/x-data-grid";
import { useNavigate, useParams } from "react-router-dom";
import {
  arrayRemove, arrayUnion, collection, deleteField, doc, getDoc,
  getDocs, onSnapshot, query, serverTimestamp, Timestamp, updateDoc, where,
} from "firebase/firestore";
import { db } from "../firebase";
import { useSurveyAccess } from "../hooks/useSurveyAccess";
import PageHeader from "../components/PageHeader";

// ----- Types -----
type SurveyStatus = "unpublished" | "published" | "deleted";
type Role = "owner" | "editor" | "viewer" | "admin" | "none";

type SurveyDoc = {
  title?: string;
  name?: string;
  ownerId?: string;
  userId?: string;
  createdAt?: Timestamp | Date | null;
  updatedAt?: Timestamp | Date | null;
  status?: SurveyStatus;
  published?: boolean;
  isPublished?: boolean;
  roles?: Record<string, Role>;
  memberIds?: string[];
  publicPath?: string;
  questionCount?: number;
  responseCount?: number;
  viewerSettings?: {
    progressBar?: "default" | "percentage";
  };
};

type Snack = {
  open: boolean;
  message: string;
  severity: "success" | "info" | "warning" | "error";
};

type UserDirectory = {
  uid: string;
  email: string;
  emailLower?: string;
  firstName?: string;
  lastName?: string;
  displayName?: string;
};

// ---------- Gate wrapper (keeps hook order clean) ----------
export default function SurveyManager() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();

  const { loading: aclLoading, allowed, role } = useSurveyAccess(id!, "manage");

  if (aclLoading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>Loading…</Typography>
      </Box>
    );
  }

  if (!allowed) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Survey not found or access denied.</Typography>
        <Button sx={{ mt: 2 }} variant="outlined" onClick={() => nav("/surveys")}>
          Back to Surveys
        </Button>
      </Box>
    );
  }

  return <SurveyManagerInner surveyId={id!} accessRole={role ?? "none"} />;
}

// ---------- Inner component (all hooks always run) ----------
function SurveyManagerInner({
  surveyId,
  accessRole,
}: {
  surveyId: string;
  accessRole: Role;
}) {
  const navigate = useNavigate();

  const [survey, setSurvey] = useState<SurveyDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const [snack, setSnack] = useState<Snack>({ open: false, message: "", severity: "success" });

  // Roles: search and assignment state
  const [roleSearchEmail, setRoleSearchEmail] = useState("");
  const [searching, setSearching] = useState(false);
  const [foundUser, setFoundUser] = useState<UserDirectory | null>(null);
  const [selectedRole, setSelectedRole] = useState<Role>("viewer");
  const [notFoundMsg, setNotFoundMsg] = useState<string>("");

  // Profiles cache for current members (uid -> UserDirectory)
  const [memberProfiles, setMemberProfiles] = useState<Record<string, UserDirectory>>({});

  // ---- Load survey ----
  useEffect(() => {
    const ref = doc(collection(db, "surveys"), surveyId);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setSurvey(null);
          setLoading(false);
          return;
        }
        const data = snap.data() as SurveyDoc;

        const toDate = (v: any): Date | undefined =>
          v instanceof Timestamp ? v.toDate() : v instanceof Date ? v : undefined;

        // Normalize ownerId from either ownerId or userId
        const ownerId = data.ownerId ?? data.userId ?? undefined;

        setSurvey({
          ...data,
          ownerId,
          createdAt: toDate(data.createdAt),
          updatedAt: toDate(data.updatedAt),
        });
        setLoading(false);
      },
      (err) => {
        console.error("SurveyManager: onSnapshot error", err);
        setSnack({ open: true, message: "Failed to load survey.", severity: "error" });
        setLoading(false);
      }
    );

    return () => unsub();
  }, [surveyId]);

  // Derived flags
  const isPublished = !!(
    (survey?.status === "published") ||
    (survey?.status === undefined && (survey?.published || survey?.isPublished))
  );
  const isDeleted = survey?.status === "deleted";

  const title = survey?.title || survey?.name || "(Untitled survey)";
  const ownerUid = survey?.ownerId ?? survey?.userId ?? ""; // single source of truth

  const publicUrl = useMemo(() => {
    const base = window.location.origin;
    const path = survey?.publicPath ? survey.publicPath : `/view/${surveyId}`;
    return `${base}${path.startsWith("/") ? "" : "/"}${path}`;
  }, [surveyId, survey?.publicPath]);

  const progressBarStyle = survey?.viewerSettings?.progressBar ?? "default";

  const handleProgressBarStyleChange = async (progressBar: "default" | "percentage") => {
    try {
      await updateDoc(doc(db, "surveys", surveyId), {
        "viewerSettings.progressBar": progressBar,
        updatedAt: serverTimestamp(),
      });
      setSnack({ open: true, message: "Progress bar setting updated.", severity: "success" });
    } catch (e) {
      console.error("SurveyManager: progress bar setting error", e);
      setSnack({ open: true, message: "Failed to update progress bar setting.", severity: "error" });
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setSnack({ open: true, message: "Public link copied.", severity: "success" });
    } catch {
      setSnack({ open: true, message: "Failed to copy link.", severity: "error" });
    }
  };

  const handlePublishToggle = async () => {
    if (isDeleted) return;
    const next: SurveyStatus = isPublished ? "unpublished" : "published";
    try {
      await updateDoc(doc(db, "surveys", surveyId), {
        status: next,
        published: next === "published", // legacy support
        updatedAt: serverTimestamp(),
      });
      setSnack({
        open: true,
        message: next === "published" ? "Survey published." : "Survey unpublished.",
        severity: "success",
      });
    } catch (e) {
      console.error("SurveyManager: publish toggle error", e);
      setSnack({ open: true, message: "Failed to update publish status.", severity: "error" });
    }
  };

  const handleEdit = () => navigate(`/surveys/${surveyId}/edit`);

  const handleSoftDelete = async () => {
    if (!window.confirm("Move this survey to Deleted Items? (It will no longer be visible to respondents.)")) {
      return;
    }
    try {
      await updateDoc(doc(db, "surveys", surveyId), {
        status: "deleted",
        published: false,
        updatedAt: serverTimestamp(),
      });
      setSnack({ open: true, message: "Survey moved to Deleted Items.", severity: "success" });
      navigate("/surveys");
    } catch (e) {
      console.error("SurveyManager: soft delete error", e);
      setSnack({ open: true, message: "Failed to delete survey.", severity: "error" });
    }
  };

  // -------- Roles: helpers --------
  const resolveDisplay = (u: UserDirectory | undefined) => {
    if (!u) return "";
    const name = (u.displayName || `${u.firstName || ""} ${u.lastName || ""}`.trim()).trim();
    return name || u.email || u.uid;
  };

  // Load profiles for current members into cache
  useEffect(() => {
    const load = async () => {
      const uids = Object.keys(survey?.roles || {});
      if (uids.length === 0) {
        setMemberProfiles({});
        return;
      }
      const out: Record<string, UserDirectory> = {};
      await Promise.all(
        uids.map(async (uid) => {
          try {
            const snap = await getDoc(doc(db, "users", uid));
            if (snap.exists()) {
              const d = snap.data() as any;
              out[uid] = {
                uid,
                email: d.email || "",
                emailLower: d.emailLower || d.email?.toLowerCase?.(),
                firstName: d.firstName,
                lastName: d.lastName,
                displayName: d.displayName,
              };
            } else {
              out[uid] = { uid, email: "" };
            }
          } catch {
            out[uid] = { uid, email: "" };
          }
        })
      );
      setMemberProfiles(out);
    };
    load();
  }, [survey?.roles]);

  // Search a single user by exact email (case-insensitive)
  const searchUsers = async () => {
    const termRaw = roleSearchEmail.trim();
    const term = termRaw.toLowerCase();
    setFoundUser(null);
    setNotFoundMsg("");
    if (!term) return;

    setSearching(true);
    try {
      const usersCol = collection(db, "users");
      const qy = query(usersCol, where("emailLower", "==", term));
      const snap = await getDocs(qy);

      if (snap.empty) {
        setNotFoundMsg(`No user found for ${termRaw}`);
        return;
      }

      const d = snap.docs[0];
      const v = d.data() as any;
      const u: UserDirectory = {
        uid: d.id,
        email: v.email || "",
        emailLower: v.emailLower || v.email?.toLowerCase?.(),
        firstName: v.firstName,
        lastName: v.lastName,
        displayName: v.displayName,
      };

      // Owner special case (using normalized ownerUid)
      if (u.uid === ownerUid) {
        setNotFoundMsg("This is your survey — you already own it.");
        return;
      }

      // Already a member (any role) special case
      const existingRole = survey?.roles?.[u.uid] as Role | undefined;
      if (existingRole) {
        setNotFoundMsg(`Already a member (${existingRole}).`);
        return;
      }

      setFoundUser(u);
    } catch (e) {
      console.error("SurveyManager: user search failed", e);
      setSnack({ open: true, message: "User search failed.", severity: "error" });
    } finally {
      setSearching(false);
    }
  };

  // Assign role to found user
  const assignRole = async () => {
    if (!foundUser) {
      setSnack({ open: true, message: "Search and select a user first.", severity: "warning" });
      return;
    }
    if (foundUser.uid === ownerUid) {
      setSnack({ open: true, message: "You already own this survey.", severity: "info" });
      return;
    }

    // Avoid adding a user twice
    const existingRole = survey?.roles?.[foundUser.uid] as Role | undefined;
    if (existingRole) {
      setSnack({ open: true, message: `Already a member (${existingRole}).`, severity: "info" });
      return;
    }

    try {
      const ref = doc(db, "surveys", surveyId);
      await updateDoc(ref, {
        [`roles.${foundUser.uid}`]: selectedRole,  // single role
        memberIds: arrayUnion(foundUser.uid),      // duplicated list
        updatedAt: serverTimestamp(),
      });
      setSnack({ open: true, message: "Role assigned.", severity: "success" });
      setFoundUser(null);
      setRoleSearchEmail("");
      setSelectedRole("viewer");
      setNotFoundMsg("");
    } catch (e) {
      console.error("SurveyManager: assign role error", e);
      setSnack({ open: true, message: "Failed to assign role.", severity: "error" });
    }
  };

  // Change role of an existing member (ignore no-op)
  const changeRole = async (uid: string, newRole: Role) => {
    if (uid === ownerUid) return; // owner protected

    const current = survey?.roles?.[uid] as Role | undefined;
    if (current === newRole) {
      return; // no change, no write
    }

    try {
      const ref = doc(db, "surveys", surveyId);
      await updateDoc(ref, {
        [`roles.${uid}`]: newRole,
        updatedAt: serverTimestamp(),
      });
      setSnack({ open: true, message: "Role updated.", severity: "success" });
    } catch (e) {
      console.error("SurveyManager: change role error", e);
      setSnack({ open: true, message: "Failed to update role.", severity: "error" });
    }
  };

  // Remove a member
  const removeMember = async (uid: string) => {
    if (uid === ownerUid) return; // cannot remove owner
    if (!window.confirm("Remove this member from the survey?")) return;

    try {
      const ref = doc(db, "surveys", surveyId);
      await updateDoc(ref, {
        [`roles.${uid}`]: deleteField(),
        memberIds: arrayRemove(uid),
        updatedAt: serverTimestamp(),
      });
      setSnack({ open: true, message: "Member removed.", severity: "success" });
    } catch (e) {
      console.error("SurveyManager: remove member error", e);
      setSnack({ open: true, message: "Failed to remove member.", severity: "error" });
    }
  };

  // ---- Table of current members ----
  type MemberRow = { id: string; name: string; email: string; role: Role; isOwner: boolean };

  const memberRows: MemberRow[] = useMemo(() => {
    const entries = Object.entries(survey?.roles || {}); // [uid, role]
    return entries.map(([uid, role]) => {
      const prof = memberProfiles[uid];
      const name = resolveDisplay(prof);
      const email = prof?.email || "";
      const isOwner = uid === ownerUid;
      return { id: uid, name, email, role: role as Role, isOwner };
    });
  }, [survey?.roles, ownerUid, memberProfiles]);

  const memberCols: GridColDef<MemberRow>[] = [
    { field: "name", headerName: "User", flex: 1, minWidth: 200 },
    { field: "email", headerName: "Email", flex: 1, minWidth: 220 },
    {
      field: "role",
      headerName: "Role",
      width: 160,
      renderCell: ({ row }) => {
        if (row.isOwner) return <span>owner</span>;
        return (
          <Select
            size="small"
            value={row.role}
            onChange={(e) => changeRole(row.id, e.target.value as Role)}
          >
            <MenuItem value="viewer">viewer</MenuItem>
            <MenuItem value="editor">editor</MenuItem>
          </Select>
        );
      },
      sortable: false,
      filterable: false,
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      renderCell: ({ row }) =>
        row.isOwner ? null : (
          <Tooltip title="Remove member">
            <span>
              <IconButton onClick={() => removeMember(row.id)}>
                <RemoveCircleOutlineIcon />
              </IconButton>
            </span>
          </Tooltip>
        ),
      sortable: false,
      filterable: false,
    },
  ];

  return (
    <Box sx={{ p: 2, display: "flex", flexDirection: "column", minHeight: "calc(100vh - 96px)" }}>
      <PageHeader
        onBack={() => navigate("/surveys")}
        backAriaLabel="Back to surveys"
        title="Survey Manager"
        subtitle={survey?.title ? `Title: ${survey.title}` : undefined}
      />

      {/* Main content */}
      <Box sx={{ flex: 1 }}>
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: { xs: "1fr", md: "7fr 5fr" },
            alignItems: "start",
          }}
        >
          {/* LEFT PANEL */}
          <Box>
            <Card variant="outlined">
              <CardContent>
                <Stack spacing={2}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h5">
                      {loading ? "Loading..." : title}
                    </Typography>
                    <Chip
                      label={isPublished ? "Published" : "Unpublished"}
                      color={isPublished ? "success" : "default"}
                      variant={isPublished ? "filled" : "outlined"}
                    />
                  </Stack>

                  {/* Timestamps + counts (no IDs here anymore) */}
                  <Stack direction="row" spacing={2} flexWrap="wrap">
                    <Typography variant="body2">
                      <strong>Last updated:</strong>{" "}
                      {survey?.updatedAt instanceof Date ? survey.updatedAt.toLocaleString() : "—"}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Created:</strong>{" "}
                      {survey?.createdAt instanceof Date ? survey.createdAt.toLocaleString() : "—"}
                    </Typography>
                    {typeof survey?.questionCount === "number" && (
                      <Typography variant="body2">
                        <strong>Questions:</strong> {survey.questionCount}
                      </Typography>
                    )}
                    {typeof survey?.responseCount === "number" && (
                      <Typography variant="body2">
                        <strong>Responses:</strong> {survey.responseCount}
                      </Typography>
                    )}
                  </Stack>

                  <Divider />

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "stretch", sm: "center" }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 140 }}>
                      Progress bar
                    </Typography>
                    <TextField
                      select
                      size="small"
                      value={progressBarStyle}
                      onChange={(e) => handleProgressBarStyleChange(e.target.value as "default" | "percentage")}
                      disabled={loading || !survey}
                      sx={{ minWidth: 260 }}
                    >
                      <MenuItem value="default">Default SurveyJS progress bar</MenuItem>
                      <MenuItem value="percentage">Percentage progress bar</MenuItem>
                    </TextField>
                  </Stack>

                  <Divider />

                  {/* Public link */}
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                      <strong>Public link:</strong>{" "}
                      {isPublished && publicUrl ? (
                        <MLink href={publicUrl} target="_blank" rel="noopener">
                          {publicUrl}
                        </MLink>
                      ) : (
                        "—"
                      )}
                    </Typography>
                    <Tooltip title="Copy link">
                      <span>
                        <IconButton
                          onClick={handleCopyLink}
                          disabled={!isPublished || !publicUrl}
                          aria-label="Copy link"
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Open link">
                      <span>
                        <IconButton
                          component="a"
                          href={publicUrl}
                          target="_blank"
                          rel="noopener"
                          disabled={!isPublished || !publicUrl}
                          aria-label="Open link"
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Box>

          {/* RIGHT PANEL: Quick Actions */}
          <Box>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Quick Actions
                </Typography>
                <Stack spacing={1}>
                  <Button
                    fullWidth
                    variant={isPublished ? "outlined" : "contained"}
                    onClick={handlePublishToggle}
                    startIcon={isPublished ? <UnpublishedIcon /> : <PublishIcon />}
                    disabled={loading || isDeleted}
                  >
                    {isPublished ? "Unpublish" : "Publish"}
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleEdit}
                    startIcon={<EditIcon />}
                    disabled={!surveyId}
                  >
                    Edit survey
                  </Button>
                  <Tooltip title="Moves this survey to Deleted Items (soft delete)">
                    <span>
                      <Button
                        fullWidth
                        variant="outlined"
                        color="error"
                        onClick={handleSoftDelete}
                        startIcon={<DeleteForeverIcon />}
                        disabled={!surveyId}
                      >
                        Delete survey
                      </Button>
                    </span>
                  </Tooltip>
                </Stack>
              </CardContent>
            </Card>
          </Box>

          {/* ROLES SECTION */}
          <Box sx={{ gridColumn: "1 / -1" }}>
            <Card variant="outlined">
              <CardContent>
                <Stack spacing={2}>
                  <Typography variant="h6">Survey Roles</Typography>

                  {/* Assign new role */}
                  <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems="center">
                    <TextField
                      fullWidth
                      size="small"
                      label="Search by email"
                      placeholder="user@example.com"
                      value={roleSearchEmail}
                      onChange={(e) => setRoleSearchEmail(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") searchUsers(); }}
                    />
                    <Button variant="outlined" onClick={searchUsers} disabled={!roleSearchEmail.trim() || searching}>
                      Search
                    </Button>

                    <TextField
                      select
                      size="small"
                      label="Role"
                      value={selectedRole}
                      onChange={(e) => setSelectedRole(e.target.value as Role)}
                      sx={{ minWidth: 140 }}
                    >
                      <MenuItem value="viewer">viewer</MenuItem>
                      <MenuItem value="editor">editor</MenuItem>
                    </TextField>

                    <Button
                      variant="contained"
                      startIcon={<PersonAddAltIcon />}
                      onClick={assignRole}
                      disabled={!foundUser}
                    >
                      Assign
                    </Button>
                  </Stack>

                  {/* Search feedback */}
                  {notFoundMsg && (
                    <Typography variant="body2" color="text.secondary">{notFoundMsg}</Typography>
                  )}

                  {foundUser && (
                    <Box
                      sx={{
                        mt: 1,
                        p: 1,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {(foundUser.displayName || `${foundUser.firstName || ""} ${foundUser.lastName || ""}`.trim()) || foundUser.email}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">{foundUser.email}</Typography>
                      </Box>
                      <Chip label="Ready to assign" size="small" />
                    </Box>
                  )}

                  {/* Current members table */}
                  <div style={{ width: "100%" }}>
                    <DataGrid
                      rows={memberRows}
                      columns={memberCols}
                      autoHeight
                      pageSizeOptions={[5, 10]}
                      initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
                      disableRowSelectionOnClick
                      slots={{ toolbar: GridToolbar }}
                      slotProps={{ toolbar: { showQuickFilter: true, printOptions: { disableToolbarButton: true } } }}
                    />
                  </div>
                </Stack>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Box>

      {/* Footer: IDs + access (tucked at bottom) */}
      <Box sx={{ mt: 3, textAlign: "center", color: "text.secondary" }}>
        <Typography variant="caption" display="block">Owner User ID: {ownerUid || "—"}</Typography>
        <Typography variant="caption" display="block">Survey ID: {surveyId}</Typography>
        <Typography variant="caption" display="block">Your access: {accessRole}</Typography>
      </Box>

      <Snackbar
        open={snack.open}
        autoHideDuration={2500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snack.severity} variant="filled">
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
